/**
 * Light Board rules (WP3-LIGHTBOARD; research library sched-flow.md 4.1-4.3,
 * Pulse Status StatusBoard/QuickButtons semantics). Pure functions, no IO:
 * the API applies them inside its transaction and the web renders with them,
 * so the server and every screen agree on ages, escalation and the queue.
 */
import {
  DEFAULT_QUICK_BUTTONS,
  OFFICE_KINDS,
  PRIORITIES,
  PROTECTED_BUTTON_IDS,
  REPLAY_MAX_AGE_MS,
  type Ageing,
  type ClearReason,
  type LightboardSettings,
  type Priority,
  type QuickButton,
  type RoomState,
  type SignalKind,
} from '@velora/contracts/lightboard';

/* ------------------------------------------------------------------------ */
/* Ageing (Pulse Status card bar)                                            */
/* ------------------------------------------------------------------------ */

export type AgeBucket = 'fresh' | 'warning' | 'delayed' | 'critical';

/**
 * Bucket for an elapsed time. Pulse Status: under 3 min fresh, 3-7 warning,
 * 7-12 delayed, 12 and up critical (the pulsing ring). Recompute every tick;
 * never memoise on the start time.
 */
export function ageBucket(elapsedMs: number, ageing: Ageing): AgeBucket {
  const min = Math.max(0, elapsedMs) / 60_000;
  if (min >= ageing.criticalMin) return 'critical';
  if (min >= ageing.delayedMin) return 'delayed';
  if (min >= ageing.warnMin) return 'warning';
  return 'fresh';
}

/** Glyph for colour-blind users, paired with the bucket colour (sched-flow 4.2). */
export const AGE_GLYPH: Readonly<Record<AgeBucket, string>> = {
  fresh: '',
  warning: 'quarter',
  delayed: 'half',
  critical: 'full',
};

/** `MM:SS` under an hour, `H:MM:SS` after; negative input reads 00:00. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/* ------------------------------------------------------------------------ */
/* Clock skew (SF-04)                                                        */
/* ------------------------------------------------------------------------ */

/**
 * Offset to add to the local clock to get server time, from one request:
 * the server stamped `serverNowMs` somewhere between `sentAtMs` and
 * `receivedAtMs` (local), so assume the midpoint.
 */
export function clockOffset(serverNowMs: number, sentAtMs: number, receivedAtMs: number): number {
  const mid = sentAtMs + (receivedAtMs - sentAtMs) / 2;
  return Math.round(serverNowMs - mid);
}

/* ------------------------------------------------------------------------ */
/* Patient label (minimum necessary)                                         */
/* ------------------------------------------------------------------------ */

export type PatientLabelMode = 'first_initial' | 'initials';

/**
 * "Jenna T." by default; "J. T." for waiting-room screens and privacy mode.
 * Uses the preferred name when there is one. Never the full last name.
 */
export function patientLabel(
  firstName: string,
  lastName: string,
  preferredName: string | null,
  mode: PatientLabelMode,
): string {
  const first = (preferredName?.trim() || firstName.trim()).split(/\s+/)[0] ?? '';
  const lastInitial = lastName.trim().charAt(0).toUpperCase();
  if (mode === 'initials') {
    const fi = first.charAt(0).toUpperCase();
    return [fi && `${fi}.`, lastInitial && `${lastInitial}.`].filter(Boolean).join(' ');
  }
  if (!first) return lastInitial ? `${lastInitial}.` : '';
  return lastInitial ? `${first} ${lastInitial}.` : first;
}

/* ------------------------------------------------------------------------ */
/* Binding a room to its appointment                                         */
/* ------------------------------------------------------------------------ */

export interface ApptCandidate {
  id: string;
  startsAt: Date;
  lengthMin: number;
  flowStatus: 'scheduled' | 'late' | 'here' | 'ready' | 'chair' | 'checkout' | 'complete';
}

/** Flow statuses that mean "this patient is in or for this room right now", best first. */
const ACTIVE_ORDER = ['chair', 'checkout', 'ready', 'here'] as const;

/**
 * The appointment a room is about right now (sched-flow 4.3: "Pt Ready Op 3"
 * links to the appointment). Order: in the chair, at checkout, ready, here;
 * then the visit whose time covers now; then the next one starting within
 * `lookaheadMin`. Completed visits never bind.
 */
export function bindAppointment<T extends ApptCandidate>(
  candidates: readonly T[],
  now: Date,
  lookaheadMin = 60,
): T | null {
  const open = candidates.filter((c) => c.flowStatus !== 'complete');
  for (const status of ACTIVE_ORDER) {
    const hits = open.filter((c) => c.flowStatus === status);
    if (hits.length > 0) return earliest(hits);
  }
  const t = now.getTime();
  const covering = open.filter(
    (c) => c.startsAt.getTime() <= t && t < c.startsAt.getTime() + c.lengthMin * 60_000,
  );
  if (covering.length > 0) return earliest(covering);
  const upcoming = open.filter(
    (c) => c.startsAt.getTime() > t && c.startsAt.getTime() - t <= lookaheadMin * 60_000,
  );
  return upcoming.length > 0 ? earliest(upcoming) : null;
}

function earliest<T extends ApptCandidate>(list: readonly T[]): T {
  return [...list].sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())[0] as T;
}

/* ------------------------------------------------------------------------ */
/* Room state                                                                */
/* ------------------------------------------------------------------------ */

export interface StoredRoomState {
  state: RoomState;
  since: Date;
}

/** Room states that mean a patient is in the chair. */
const OCCUPIED_ROOM_STATES: readonly RoomState[] = [
  'seated',
  'needs_doctor',
  'doctor_in',
  'needs_assist',
];

/** The room a flow status implies when nobody set one (NEW-WF-06, WF-03). */
export function roomStateFromFlow(flow: ApptCandidate['flowStatus'] | null): RoomState {
  if (flow === 'chair') return 'seated';
  if (flow === 'checkout') return 'turnover';
  return 'empty';
}

/**
 * What a room shows. A state set before today's local midnight is stale (the
 * board starts each day clean). With no state set today the room is derived
 * from its appointment: in the chair reads Seated, at checkout Turnover,
 * else Open.
 *
 * A room state older than the bound appointment's latest flow step yields to
 * the flow (WF-03): after "Room clean" at 09:50, the next patient seated from
 * the flow board at 10:05 shows Seated, not Open.
 */
export function effectiveRoomState(
  stored: StoredRoomState | null,
  dayStart: Date,
  boundFlow: ApptCandidate['flowStatus'] | null,
  boundFlowAt: Date | null = null,
): { state: RoomState; since: Date | null; derived: boolean } {
  if (stored && stored.since.getTime() >= dayStart.getTime()) {
    const flowIsNewer =
      boundFlow !== null && boundFlowAt !== null && boundFlowAt.getTime() > stored.since.getTime();
    const fromFlow = roomStateFromFlow(boundFlow);
    // A newer flow step yields only when it says something about the room:
    // in the chair (Seated) or at checkout (Turnover), or it takes the
    // patient out of an occupied room (a step back). Checking in the next
    // patient never turns a Turnover or Clean room back to Open.
    const yields =
      flowIsNewer && (fromFlow !== 'empty' || OCCUPIED_ROOM_STATES.includes(stored.state));
    if (!yields) return { state: stored.state, since: stored.since, derived: false };
    return { state: fromFlow, since: boundFlowAt, derived: true };
  }
  return { state: roomStateFromFlow(boundFlow), since: null, derived: true };
}

/** The room-bound signal kind a state opens, if any. */
export function signalForState(state: RoomState): SignalKind | null {
  if (state === 'needs_doctor') return 'needs_doctor';
  if (state === 'needs_assist') return 'needs_assist';
  return null;
}

/**
 * Leaving a state closes its signal. Going to Doctor in closes a doctor
 * request with reason `doctor_in` (the doctor-wait interval ends, SF-09).
 */
export function clearReasonForTransition(next: RoomState): ClearReason {
  return next === 'doctor_in' ? 'doctor_in' : 'state_changed';
}

/* ------------------------------------------------------------------------ */
/* Signals: priority, escalation, expiry, queue                              */
/* ------------------------------------------------------------------------ */

export function priorityRank(p: Priority): number {
  return PRIORITIES.indexOf(p);
}

export function defaultPriority(kind: SignalKind): Priority {
  switch (kind) {
    case 'emergency':
      return 'emergency';
    case 'urgent':
      return 'urgent';
    case 'needs_doctor':
    case 'phone':
      return 'high';
    case 'xray':
      return 'low';
    default:
      return 'normal';
  }
}

export function isOfficeKind(kind: SignalKind): boolean {
  return OFFICE_KINDS.includes(kind);
}

export interface SignalTiming {
  kind: SignalKind;
  priority: Priority;
  raisedAt: Date;
  claimedAt: Date | null;
}

/**
 * Unclaimed and older than the office threshold (SF-10). Low priority never
 * escalates; urgent and emergency are already office-wide.
 */
export function isEscalated(s: SignalTiming, now: Date, escalateAfterMin: number): boolean {
  if (s.claimedAt !== null) return false;
  if (s.priority === 'low') return false;
  return now.getTime() - s.raisedAt.getTime() >= escalateAfterMin * 60_000;
}

/**
 * Low and normal quick-button signals that nobody claimed fall off the board
 * (Pulse Status auto-expiry). Room-bound kinds live as long as the room state;
 * high and above never expire silently.
 */
export function isExpired(s: SignalTiming, now: Date, expireAfterMin: number): boolean {
  if (s.kind === 'needs_doctor' || s.kind === 'needs_assist') return false;
  if (priorityRank(s.priority) > priorityRank('normal')) return false;
  return now.getTime() - s.raisedAt.getTime() >= expireAfterMin * 60_000;
}

export interface QueueItem {
  id: string;
  raisedAt: Date;
  claimedAt: Date | null;
  /** Doctor the request is for (appointment or room provider), null = any doctor. */
  providerId: string | null;
}

/**
 * Doctor queue (SF-08): unclaimed `needs_doctor` requests in raise order, per
 * provider. Position 1 is next in line. Claimed requests leave the queue.
 */
export function doctorQueue(items: readonly QueueItem[]): Map<string, number> {
  const byProvider = new Map<string, QueueItem[]>();
  for (const it of items) {
    if (it.claimedAt !== null) continue;
    const key = it.providerId ?? '*';
    const list = byProvider.get(key) ?? [];
    list.push(it);
    byProvider.set(key, list);
  }
  const out = new Map<string, number>();
  for (const list of byProvider.values()) {
    list
      .sort((a, b) => a.raisedAt.getTime() - b.raisedAt.getTime() || a.id.localeCompare(b.id))
      .forEach((it, i) => out.set(it.id, i + 1));
  }
  return out;
}

/** Seconds a doctor request waited: until claimed, else cleared, else now. */
export function doctorWaitSeconds(
  raisedAt: Date,
  claimedAt: Date | null,
  clearedAt: Date | null,
  now: Date,
): number {
  const end = claimedAt ?? clearedAt ?? now;
  return Math.max(0, Math.round((end.getTime() - raisedAt.getTime()) / 1000));
}

/* ------------------------------------------------------------------------ */
/* Frame time (replay)                                                       */
/* ------------------------------------------------------------------------ */

export type FrameTime = { ok: true; at: Date } | { ok: false; code: 'too_old' };

/**
 * Live frames are server-stamped. Replayed frames keep their original time
 * (SF-18), clamped to now when a gateway clock runs ahead, and refused when
 * older than 24 h.
 */
export function frameTime(occurredAt: string | undefined, now: Date, replay: boolean): FrameTime {
  if (!replay || occurredAt === undefined) return { ok: true, at: now };
  const at = new Date(occurredAt);
  if (Number.isNaN(at.getTime())) return { ok: true, at: now };
  if (now.getTime() - at.getTime() > REPLAY_MAX_AGE_MS) return { ok: false, code: 'too_old' };
  return { ok: true, at: at.getTime() > now.getTime() ? now : at };
}

/* ------------------------------------------------------------------------ */
/* Buttons                                                                   */
/* ------------------------------------------------------------------------ */

/** The buttons an office shows: defaults minus hidden (never protected), then custom. */
export function resolveButtons(settings: LightboardSettings): QuickButton[] {
  const hidden = new Set(settings.hiddenButtons.filter((id) => !PROTECTED_BUTTON_IDS.includes(id)));
  const defaults = DEFAULT_QUICK_BUTTONS.filter((b) => !hidden.has(b.id));
  const custom: QuickButton[] = settings.customButtons.map((c) => ({
    id: c.id,
    label: c.label,
    detail: c.detail,
    icon: c.scope === 'office' ? 'megaphone' : 'message-square',
    action: { type: 'signal', kind: 'custom', priority: c.priority },
    scope: c.scope,
    protected: false,
    custom: true,
  }));
  // Keep Urgent and Emergency last, as in Pulse Status.
  const isLast = (b: QuickButton) => b.protected;
  return [...defaults.filter((b) => !isLast(b)), ...custom, ...defaults.filter(isLast)];
}

export function findButton(settings: LightboardSettings, id: string): QuickButton | null {
  return resolveButtons(settings).find((b) => b.id === id) ?? null;
}

/** Custom button ids must not collide with defaults or each other. */
export function validateCustomButtons(
  settings: Pick<LightboardSettings, 'customButtons'>,
): string[] {
  const issues: string[] = [];
  const seen = new Set(DEFAULT_QUICK_BUTTONS.map((b) => b.id));
  for (const c of settings.customButtons) {
    if (seen.has(c.id)) issues.push(`button id "${c.id}" is already used`);
    seen.add(c.id);
  }
  return issues;
}

/* ------------------------------------------------------------------------ */
/* Stats (ratio of sums across offices)                                      */
/* ------------------------------------------------------------------------ */

export interface AdditiveStats {
  activeSignals: number;
  needsDoctor: number;
  escalated: number;
  roomsInUse: number;
  rooms: number;
  doctorWaitSeconds: number;
  doctorWaitCount: number;
  signalsToday: number;
}

export const EMPTY_STATS: AdditiveStats = {
  activeSignals: 0,
  needsDoctor: 0,
  escalated: 0,
  roomsInUse: 0,
  rooms: 0,
  doctorWaitSeconds: 0,
  doctorWaitCount: 0,
  signalsToday: 0,
};

export function sumStats(list: readonly AdditiveStats[]): AdditiveStats {
  const out = { ...EMPTY_STATS };
  for (const s of list) {
    for (const k of Object.keys(out) as (keyof AdditiveStats)[]) out[k] += s[k];
  }
  return out;
}

/** Average doctor wait in seconds, recomputed from summed parts; null with no requests. */
export function averageDoctorWait(
  s: Pick<AdditiveStats, 'doctorWaitSeconds' | 'doctorWaitCount'>,
): number | null {
  return s.doctorWaitCount > 0 ? Math.round(s.doctorWaitSeconds / s.doctorWaitCount) : null;
}
